import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const AuthContext = createContext(null);

const STORAGE_KEY = 'sb_user';
const USERS_KEY = 'sb_users';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEY);
  }, [user]);

  // Ask backend if there's an active Google session cookie.
  const refreshFromBackend = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const u = await res.json();
        setUser({
          id: u.user_id,
          email: u.email,
          name: u.name || u.email.split('@')[0],
          picture: u.picture || '',
          provider: 'google',
        });
        return true;
      }
    } catch { /* ignore */ }
    return false;
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setChecking(false);
      return;
    }
    (async () => {
      // Prefer backend session over any stale local mock.
      const ok = await refreshFromBackend();
      if (!ok) {
        // Keep any locally-registered email/password user as-is.
      }
      setChecking(false);
    })();
  }, [refreshFromBackend]);

  const readUsers = () => {
    try { return JSON.parse(localStorage.getItem(USERS_KEY) || '[]'); } catch { return []; }
  };
  const writeUsers = (list) => localStorage.setItem(USERS_KEY, JSON.stringify(list));

  const signup = ({ email, password, name }) => {
    const users = readUsers();
    if (users.find(u => u.email.toLowerCase() === email.toLowerCase())) {
      return { ok: false, error: 'Un compte existe déjà avec cet email.' };
    }
    const newUser = {
      id: `u_${Date.now()}`,
      email: email.toLowerCase(),
      password,
      name: name || email.split('@')[0],
      provider: 'local',
      createdAt: new Date().toISOString()
    };
    users.push(newUser);
    writeUsers(users);
    const { password: _, ...safe } = newUser;
    setUser(safe);
    return { ok: true, user: safe };
  };

  const signin = ({ email, password }) => {
    const users = readUsers();
    const found = users.find(u => u.email.toLowerCase() === email.toLowerCase() && u.password === password);
    if (!found) return { ok: false, error: 'Email ou mot de passe incorrect.' };
    const { password: _, ...safe } = found;
    setUser({ ...safe, provider: 'local' });
    return { ok: true, user: safe };
  };

  const signout = async () => {
    // If Google session, kill it server-side too.
    try {
      if (user?.provider === 'google') {
        await fetch(`${BACKEND}/api/auth/logout`, { method: 'POST', credentials: 'include' });
      }
    } catch { /* ignore */ }
    setUser(null);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH. Redirect must be derived from window.location.origin.
  const startGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, checking, signup, signin, signout, startGoogleLogin, refreshFromBackend }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
